import { useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  Tooltip,
  Card,
  CardContent,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import SmsIcon from '@mui/icons-material/Sms';
import RouterIcon from '@mui/icons-material/Router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useEffectAsync } from '../../reactHelper';
import dayjs from 'dayjs';

const CommandHistoryTab = ({ t }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getStatusInfo = (status) => {
    const normalized = status?.toLowerCase() || 'pending';
    const statusMap = {
      sent: { labelKey: 'commandStatusSent', color: 'primary', icon: ScheduleIcon },
      delivered: { labelKey: 'commandStatusDelivered', color: 'success', icon: CheckCircleIcon },
      failed: { labelKey: 'commandStatusFailed', color: 'error', icon: ErrorIcon },
      pending: { labelKey: 'commandStatusPending', color: 'warning', icon: PendingIcon },
      queued: { labelKey: 'commandStatusQueued', color: 'info', icon: ScheduleIcon },
    };
    return statusMap[normalized] || statusMap.pending;
  };

  const [sentCommands, setSentCommands] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [deviceFilter, setDeviceFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [stats, setStats] = useState(null);

  const fetchCommands = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (deviceFilter) params.append('deviceId', deviceFilter.id);
      if (statusFilter) params.append('status', statusFilter);
      if (startDate) params.append('from', `${startDate}T00:00:00Z`);
      if (endDate) params.append('to', `${endDate}T23:59:59Z`);

      const response = await fetch(`/api/sent-commands?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSentCommands(data);
        calculateStats(data);
      }
    } catch (e) {
      console.error('Error fetching commands:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await fetch('/api/devices?all=true');
      if (response.ok) {
        setDevices(await response.json());
      }
    } catch (e) {
      console.error('Error fetching devices:', e);
    }
  };

  const calculateStats = (commands) => {
    const total = commands.length;
    const delivered = commands.filter((c) => c.status === 'delivered').length;
    const failed = commands.filter((c) => c.status === 'failed').length;
    const pending = commands.filter((c) => ['pending', 'sent', 'queued'].includes(c.status)).length;
    const viaSms = commands.filter((c) => c.textchannel).length;
    const viaGprs = commands.filter((c) => !c.textchannel).length;

    setStats({ total, delivered, failed, pending, viaSms, viaGprs });
  };

  useEffectAsync(async () => {
    await fetchDevices();
    await fetchCommands();
  }, []);

  const paginatedCommands = useMemo(() =>
    sentCommands.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
  [sentCommands, page, rowsPerPage]);

  const getDeviceName = (deviceId) => {
    const device = devices.find((d) => d.id === deviceId);
    return device?.name || `Device ${deviceId}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('DD/MM/YYYY HH:mm:ss');
  };

  const statusOptions = ['sent', 'delivered', 'failed', 'pending'];

  return (
    <>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">
              {t('configHistory')}
            </Typography>
            <Chip
              size="small"
              label={`${sentCommands.length} ${t('sharedTotal').toLowerCase()}`}
              color="primary"
              variant="outlined"
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCommands}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
          >
            {t('sharedRefresh')}
          </Button>
        </Box>
      </Paper>

      {/* Stats Cards */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 auto', minWidth: 100, textAlign: 'center', bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h5" color="primary.main">{stats.total}</Typography>
              <Typography variant="caption" color="text.secondary">{t('commandTotal')}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 auto', minWidth: 100, textAlign: 'center', bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h5" color="success.main">{stats.delivered}</Typography>
              <Typography variant="caption" color="text.secondary">{t('commandSuccess')}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 auto', minWidth: 100, textAlign: 'center', bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h5" color="error.main">{stats.failed}</Typography>
              <Typography variant="caption" color="text.secondary">{t('commandFailures')}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 auto', minWidth: 100, textAlign: 'center', bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h5" color="warning.main">{stats.pending}</Typography>
              <Typography variant="caption" color="text.secondary">{t('commandStatusPending')}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 auto', minWidth: 100, textAlign: 'center', bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" justifyContent="center" gap={2}>
                <Box textAlign="center">
                  <SmsIcon color="secondary" />
                  <Typography variant="body2">{stats.viaSms}</Typography>
                </Box>
                <Box textAlign="center">
                  <RouterIcon color="primary" />
                  <Typography variant="body2">{stats.viaGprs}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              type="date"
              label={t('reportFrom')}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              type="date"
              label={t('reportTo')}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Autocomplete
              size="small"
              options={devices}
              getOptionLabel={(option) => option.name || option.uniqueId}
              value={deviceFilter}
              onChange={(_, value) => {
                setDeviceFilter(value);
                setPage(0);
              }}
              slotProps={{
                paper: { sx: { minWidth: 350 } },
                listbox: { sx: { maxHeight: 350 } },
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={option.id} {...rest}>
                    {option.name || option.uniqueId}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('deviceTitle')} />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Autocomplete
              size="small"
              options={statusOptions}
              getOptionLabel={(option) => t(getStatusInfo(option).labelKey)}
              value={statusFilter}
              onChange={(_, value) => {
                setStatusFilter(value);
                setPage(0);
              }}
              slotProps={{
                paper: { sx: { minWidth: 200 } },
                listbox: { sx: { maxHeight: 250 } },
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={option} {...rest}>
                    {t(getStatusInfo(option).labelKey)}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('sharedStatus')} />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 1 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchCommands}
              disabled={loading}
              size={isMobile ? 'small' : 'medium'}
            >
              {t('reportShow')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : sentCommands.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            {t('sharedNoData')}
          </Alert>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                  <TableCell>{t('commandSentAt')}</TableCell>
                  <TableCell>{t('deviceTitle')}</TableCell>
                  {!isMobile && <TableCell>{t('sharedType')}</TableCell>}
                  <TableCell>{t('commandTitle')}</TableCell>
                  {!isMobile && <TableCell>{t('commandConnection')}</TableCell>}
                  <TableCell>{t('sharedStatus')}</TableCell>
                  {!isMobile && <TableCell>{t('commandResponse')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCommands.map((cmd) => {
                  const statusInfo = getStatusInfo(cmd.status);
                  const StatusIcon = statusInfo.icon;

                  return (
                    <TableRow
                      key={cmd.id}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(cmd.sentat)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {getDeviceName(cmd.deviceid)}
                        </Typography>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Chip
                            size="small"
                            label={cmd.type || 'custom'}
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ maxWidth: isMobile ? 120 : 250 }}>
                        <Tooltip title={cmd.command || '-'} arrow>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {cmd.command || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Chip
                            size="small"
                            icon={cmd.textchannel ? <SmsIcon /> : <RouterIcon />}
                            label={cmd.textchannel ? 'SMS' : 'GPRS'}
                            color={cmd.textchannel ? 'secondary' : 'primary'}
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          size="small"
                          icon={<StatusIcon fontSize="small" />}
                          label={t(statusInfo.labelKey)}
                          color={statusInfo.color}
                        />
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ maxWidth: 150 }}>
                          {cmd.response ? (
                            <Tooltip title={cmd.response} arrow>
                              <Typography
                                variant="caption"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                }}
                              >
                                {cmd.response}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={sentCommands.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={t('sharedRowsPerPage')}
              rowsPerPageOptions={[10, 15, 25, 50]}
            />
          </>
        )}
      </Paper>
    </>
  );
};

export default CommandHistoryTab;
